import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useClaims } from "@/hooks/use-claims";
import { useToast } from "@/hooks/use-toast";
import { useDocumentPolling } from "@/hooks/use-document-polling";
import { apiClient } from "@/lib/api-client";
import { fileToBase64, normalizeDateToYYYYMMDD, cn } from "@/lib/utils";
import { getErrorMessage } from "@/lib/errors";
import { Loader2, Upload, FileText, X } from "lucide-react";
import { Document, ClaimMetadata } from "@/types/claims";
import { SingleEstimateMetadataReview } from "./SingleEstimateMetadataReview";
import { ClaimPipelineSelect } from "./ClaimPipelineSelect";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

type EstimateKind = "insurer" | "contractor";

interface UploadSingleEstimateFormProps {
    onSuccess: (claimId: number) => void;
}

export function UploadSingleEstimateForm({ onSuccess }: UploadSingleEstimateFormProps) {
    const { createClaimAsync, updateClaimAsync } = useClaims();
    const { toast } = useToast();
    const { pollExtraction } = useDocumentPolling();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // File State
    const [file, setFile] = useState<File | null>(null);
    const [estimateKind, setEstimateKind] = useState<EstimateKind>("insurer");
    const [pipelineId, setPipelineId] = useState<number | undefined>();

    // Processing State
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState("");

    // Claim & Document State
    const [currentClaimId, setCurrentClaimId] = useState<number | null>(null);

    // Metadata State
    const [extractedMetadata, setExtractedMetadata] = useState<ClaimMetadata | null>(null);
    const [showReviewModal, setShowReviewModal] = useState(false);

    // Error State
    const [extractionError, setExtractionError] = useState<{
        message: string;
        hasPartialData: boolean;
        partialMetadata?: ClaimMetadata;
    } | null>(null);

    const [uploadError, setUploadError] = useState<{
        message: string;
        details?: string;
    } | null>(null);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
        let selectedFile: File | undefined;

        if ("target" in e && (e.target as HTMLInputElement).files) {
            selectedFile = (e.target as HTMLInputElement).files?.[0];
        } else if ("dataTransfer" in e) {
            selectedFile = e.dataTransfer.files?.[0];
        }

        if (!selectedFile) return;

        const MAX_FILE_SIZE = 50 * 1024 * 1024;
        if (selectedFile.size > MAX_FILE_SIZE) {
            toast({
                title: "File too large",
                description: "The selected file exceeds the maximum size of 50MB.",
                variant: "destructive",
            });
            return;
        }

        setFile(selectedFile);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        handleFileUpload(e);
    };

    const removeFile = () => {
        setFile(null);
        // Keep currentClaimId so a retry reuses the existing draft
        // instead of creating an orphaned claim
        // Clear the file input so re-selecting the same file triggers onChange
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const initializeDraftClaim = async (): Promise<number> => {
        if (currentClaimId) return currentClaimId;

        const uniqueId = crypto.randomUUID();
        const draft = await createClaimAsync({
            claimNumber: `DRAFT-${uniqueId}`,
            insuredName: "Draft Claim",
            pipelineId,
            claimStatusTemplateId: null,
            description: "Draft claim created from single estimate upload",
        });

        if (!draft?.id) throw new Error("Failed to create draft claim");
        setCurrentClaimId(draft.id);
        return draft.id;
    };

    const uploadDocument = async (uploadFile: File, kind: EstimateKind, claimId: number): Promise<Document> => {
        const base64 = await fileToBase64(uploadFile);

        const payload = {
            createDocumentRequest: {
                filename: uploadFile.name,
                mimeType: (uploadFile.type || "application/pdf") as import("@/api/generated/client/models/CreateDocumentRequest").CreateDocumentRequestMimeTypeEnum,
                sizeBytes: uploadFile.size,
                kind,
                fileContent: base64,
                claimId,
            },
        };

        try {
            return await apiClient.documents.createDocument(payload);
        } catch (error: any) {
            const status = error?.response?.status || error?.status;
            const errorCode = error?.response?.data?.error?.code || error?.body?.error?.code;

            if (status === 413 || errorCode === "PAYLOAD_TOO_LARGE") {
                const errorMessage =
                    error?.response?.data?.error?.message ||
                    error?.body?.error?.message ||
                    "File size exceeds the maximum allowed limit";
                const maxSizeMB =
                    error?.response?.data?.error?.details?.maxSizeMB ||
                    error?.body?.error?.details?.maxSizeMB ||
                    "50MB";
                const err = new Error(`${errorMessage}. Maximum file size: ${maxSizeMB}`);
                (err as any).isFileSizeError = true;
                (err as any).maxSize = maxSizeMB;
                throw err;
            }

            throw error;
        }
    };

    const processFile = async () => {
        if (!file) return;

        setIsProcessing(true);

        try {
            setProcessingStatus("Creating claim...");
            const claimId = await initializeDraftClaim();

            setProcessingStatus("Uploading document...");
            const doc = await uploadDocument(file, estimateKind, claimId);

            // Poll for metadata extraction using shared hook
            // 150 attempts × 2s = 5 minutes timeout
            const metadata = await pollExtraction(doc.id, {
                maxAttempts: 150,
                onStatusChange: setProcessingStatus,
            });

            setExtractedMetadata(metadata ?? {});
            setShowReviewModal(true);
            setIsProcessing(false);
            setProcessingStatus("");
        } catch (error: any) {
            console.error("Processing Error:", error);

            if (error?.isFileSizeError) {
                setUploadError({
                    message: getErrorMessage(error),
                    details: error?.maxSize ? `Maximum allowed file size is ${error.maxSize}` : undefined,
                });
            } else if (
                getErrorMessage(error).includes("timed out") ||
                getErrorMessage(error).includes("Extraction failed") ||
                getErrorMessage(error).includes("failed on server")
            ) {
                // Extraction-specific errors — show dialog with manual entry option
                setExtractionError({
                    message: getErrorMessage(error),
                    hasPartialData: false,
                });
            } else {
                toast({
                    title: "Processing failed",
                    description: getErrorMessage(error),
                    variant: "destructive",
                });
            }

            setIsProcessing(false);
            setProcessingStatus("");
        }
    };

    const handleErrorCancel = () => {
        setExtractionError(null);
        setIsProcessing(false);
        setProcessingStatus("");
    };

    const handleErrorContinue = () => {
        // Open review modal with empty/partial metadata so user can enter manually
        setExtractionError(null);
        setExtractedMetadata(extractionError?.partialMetadata ?? {});
        setShowReviewModal(true);
        setIsProcessing(false);
        setProcessingStatus("");
    };

    const handleUploadErrorClose = () => {
        setUploadError(null);
        setIsProcessing(false);
        setProcessingStatus("");
    };

    const handleMetadataConfirm = async (finalMetadata: ClaimMetadata) => {
        if (!currentClaimId) return;

        setIsProcessing(true);
        try {
            setProcessingStatus("Updating claim with metadata...");
            await updateClaimAsync({
                claimId: currentClaimId,
                updateClaimRequest: {
                    insuredName: finalMetadata.insuredName || finalMetadata.claimNumber || "Unknown Insured",
                    propertyAddress: finalMetadata.insuredAddress,
                    insuredPhone: finalMetadata.insuredPhone,
                    insuredEmail: finalMetadata.insuredEmail,
                    claimNumber: finalMetadata.claimNumber || "PENDING",
                    policyNumber: finalMetadata.policyNumber,
                    dateOfLoss: normalizeDateToYYYYMMDD(finalMetadata.dateOfLoss),
                    lossType: finalMetadata.typeOfLoss as any,
                    insuranceCarrier: finalMetadata.estimatorCompany || undefined,
                    description: (finalMetadata.insuredName || "") + " - " + (finalMetadata.claimNumber || ""),
                },
                isDraftFinalization: true,
            });

            toast({ title: "Claim created successfully" });
            onSuccess(currentClaimId);
        } catch (error) {
            console.error("Failed to update claim with metadata:", error);
            toast({
                title: "Failed to create claim",
                description: getErrorMessage(error),
                variant: "destructive",
            });
        } finally {
            setIsProcessing(false);
            setProcessingStatus("");
        }
    };

    return (
        <div className="space-y-4">
            <ClaimPipelineSelect value={pipelineId} onChange={setPipelineId} disabled={isProcessing} />
            {/* Estimate Type Selector */}
            <div className="space-y-2">
                <Label>Estimate Type</Label>
                <Select value={estimateKind} onValueChange={(v) => setEstimateKind(v as EstimateKind)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select estimate type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="insurer">Insurance Estimate</SelectItem>
                        <SelectItem value="contractor">Contractor Estimate</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* File Upload Area */}
            <div
                className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/50 transition-colors cursor-pointer",
                    file ? "bg-muted/30" : ""
                )}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDrop={handleDrop}
            >
                <Label htmlFor="single-estimate-upload" className="cursor-pointer block">
                    <div className="flex flex-col items-center gap-2">
                        {file ? (
                            <>
                                <FileText className="h-8 w-8 text-blue-500" />
                                <span className="font-medium text-blue-600">{file.name}</span>
                            </>
                        ) : (
                            <>
                                <Upload className="h-8 w-8 text-muted-foreground" />
                                <span className="text-muted-foreground">Upload Estimate (PDF)</span>
                                <p className="text-xs text-muted-foreground mt-1">Drag file here or click to browse</p>
                            </>
                        )}
                    </div>
                </Label>
                <Input
                    ref={fileInputRef}
                    id="single-estimate-upload"
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={isProcessing}
                />
                {file && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={removeFile}
                        className="mt-2 text-destructive"
                        disabled={isProcessing}
                    >
                        <X className="h-4 w-4 mr-1" /> Remove
                    </Button>
                )}
            </div>

            <Button onClick={processFile} disabled={!file || isProcessing} className="w-full">
                {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isProcessing ? processingStatus : "Upload & Extract"}
            </Button>

            {/* Processing Overlay */}
            {isProcessing && !extractionError && !uploadError && (
                <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                        <Loader2 className="h-12 w-12 animate-spin text-primary" />
                        <h3 className="text-lg font-semibold">{processingStatus}</h3>
                        <p className="text-muted-foreground">Please wait while we process your document.</p>
                    </div>
                </div>
            )}

            {/* Upload Error Dialog */}
            <AlertDialog open={!!uploadError} onOpenChange={(open) => !open && handleUploadErrorClose()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>File Upload Error</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-muted-foreground space-y-4">
                                <p className="text-destructive font-medium">{uploadError?.message}</p>
                                {uploadError?.details && (
                                    <p className="text-sm text-muted-foreground">{uploadError.details}</p>
                                )}
                                <p className="text-sm">Please select a smaller PDF to upload.</p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction onClick={handleUploadErrorClose}>OK</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Extraction Error Dialog */}
            <AlertDialog open={!!extractionError} onOpenChange={(open) => !open && handleErrorCancel()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Extraction Issue Detected</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-muted-foreground space-y-4">
                                <p>We encountered an issue while extracting metadata from your document:</p>
                                <p className="font-mono text-xs bg-muted p-2 rounded text-destructive break-words">
                                    {extractionError?.message}
                                </p>
                                <p>You can cancel and try again, or continue with manual entry.</p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleErrorCancel}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleErrorContinue}>Continue with Manual Entry</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Metadata Review Modal */}
            <SingleEstimateMetadataReview
                open={showReviewModal}
                onOpenChange={setShowReviewModal}
                metadata={extractedMetadata}
                onConfirm={handleMetadataConfirm}
                isProcessing={isProcessing}
                fileName={file?.name}
            />
        </div>
    );
}
